/**
 * Telemetry Client - Analytics Provider Abstraction
 * 
 * Centralized telemetry service that abstracts the analytics provider.
 * Currently supports PostHog with easy extensibility for other providers
 * (Amplitude, Segment, Mixpanel, self-hosted, etc.)
 * 
 * Design Principles:
 * - Never blocks UI (all calls are fire-and-forget)
 * - Fails silently (errors logged to console.warn only)
 * - Respects privacy (no PII, demo mode safe)
 * - Single initialization point
 * - Type-safe event tracking
 * 
 * Configuration:
 * - VITE_POSTHOG_KEY: PostHog project API key
 * - VITE_POSTHOG_HOST: PostHog instance URL (default: https://app.posthog.com)
 * - VITE_APP_MODE: Environment mode
 * 
 * @see telemetryEvents.ts - Event type definitions
 * @see visibilityTelemetry.ts - Domain-specific wrapper
 */

import { APP_MODE, IS_DEMO_MODE } from '../../config/env';
 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { sanitizeEventData, type TelemetryEventName, type TelemetryEventPayloads } from './telemetryEvents';

// ─────────────────────────────────────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────────────────────────────────────

interface TelemetryConfig {
  /** PostHog API key */
  posthogKey: string;
  /** PostHog host URL */
  posthogHost: string;
  /** Current environment */
  environment: string;
  /** Whether telemetry is enabled */
  enabled: boolean;
  /** Enable debug logging */
  debug: boolean;
}

interface UserTraits {
  workspaceId?: string;
  workspaceRole?: string;
  companyId?: string;
  [key: string]: string | number | boolean | undefined;
}

// PostHog types (minimal interface to avoid full dependency)
interface PostHogLike {
  init: (key: string, options?: Record<string, unknown>) => void;
  capture: (event: string, properties?: Record<string, unknown>) => void;
  identify: (userId: string, properties?: Record<string, unknown>) => void;
  reset: () => void;
  opt_out_capturing: () => void;
  opt_in_capturing: () => void;
  isFeatureEnabled: (key: string) => boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// CONFIGURATION
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get telemetry configuration from environment
 */
function getConfig(): TelemetryConfig {
  // Safe env access for both Vite and Node environments
  const getEnvVar = (key: string): string => {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      return (import.meta.env as Record<string, string>)[key] || '';
    }
    if (typeof process !== 'undefined' && process.env) {
      return process.env[key] || '';
    }
    return '';
  };

  const posthogKey = getEnvVar('VITE_POSTHOG_KEY');
  const posthogHost = getEnvVar('VITE_POSTHOG_HOST') || 'https://app.posthog.com';
  const debug = getEnvVar('VITE_TELEMETRY_DEBUG') === 'true';

  // Telemetry is enabled when:
  // 1. PostHog key is configured
  // 2. NOT in demo mode (unless explicitly enabled for demo analytics)
  const enabled = Boolean(posthogKey) && !IS_DEMO_MODE;

  return {
    posthogKey,
    posthogHost,
    environment: APP_MODE,
    enabled,
    debug
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// TELEMETRY CLIENT CLASS
// ─────────────────────────────────────────────────────────────────────────────

class TelemetryClient {
  private static instance: TelemetryClient;
  private config: TelemetryConfig;
  private posthog: PostHogLike | null = null;
  private initialized = false;
  private currentUserId: string | null = null;
  private sessionId: string;
  private eventQueue: Array<{ event: string; properties: Record<string, unknown> }> = [];

  private constructor() {
    this.config = getConfig();
    this.sessionId = this.generateSessionId();

    // Auto-initialize if enabled
    if (this.config.enabled) {
      this.initialize();
    } else if (this.config.debug) {
      console.info('[telemetry] Disabled - running in demo mode or missing VITE_POSTHOG_KEY');
    }
  }

  /**
   * Get singleton instance
   */
  public static getInstance(): TelemetryClient {
    if (!TelemetryClient.instance) {
      TelemetryClient.instance = new TelemetryClient();
    }
    return TelemetryClient.instance;
  }

  /**
   * Initialize the analytics provider
   * Safe to call multiple times (no-op after first call)
   */
  private async initialize(): Promise<void> {
    if (this.initialized || !this.config.enabled) {
      return;
    }

    try {
      // Dynamic import to avoid bundling PostHog when not used
      const posthogModule = await import('posthog-js');
      this.posthog = posthogModule.default as PostHogLike;

      if (this.posthog) {
        this.posthog.init(this.config.posthogKey, {
          api_host: this.config.posthogHost,
          // Disable automatic pageview tracking (we'll do it manually if needed)
          capture_pageview: false,
          // Disable autocapture to reduce noise
          autocapture: false,
          // Respect Do Not Track browser setting
          respect_dnt: true,
          // Session recording disabled for privacy
          disable_session_recording: true,
          // Persistence across sessions
          persistence: 'localStorage',
          // Don't send IP address
          ip: false,
          // Custom properties for all events
          property_blacklist: ['$current_url', '$referrer', '$referring_domain'],
          loaded: () => {
            if (this.config.debug) {
              console.info('[telemetry] PostHog initialized successfully');
            }
            // Flush queued events
            this.flushQueue();
          }
        });

        this.initialized = true;
      }
    } catch (error) {
      // Don't throw - just log and continue without telemetry
      console.warn('[telemetry] Failed to initialize PostHog:', error);
    }
  }

  /**
   * Generate a unique session ID for grouping related events
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Flush queued events (called after initialization completes)
   */
  private flushQueue(): void {
    while (this.eventQueue.length > 0) {
      const { event, properties } = this.eventQueue.shift()!;
      this.trackInternal(event, properties);
    }
  }

  /**
   * Internal track method (after initialization)
   */
  private trackInternal(event: string, properties: Record<string, unknown>): void {
    if (!this.posthog) {
      return;
    }

    try {
      this.posthog.capture(event, properties);

      if (this.config.debug) {
        console.info('[telemetry:track]', event, properties);
      }
    } catch (error) {
      console.warn('[telemetry] Track failed:', error);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // PUBLIC API
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Track an event with optional properties
   * 
   * Type-safe overload for known events:
   * telemetry.track(TelemetryEvents.CompanyVisibilityToggled, { projectId, ... })
   * 
   * Generic overload for custom events:
   * telemetry.track('custom.event', { foo: 'bar' })
   */
  public track<E extends keyof TelemetryEventPayloads>(
    event: E,
    properties: TelemetryEventPayloads[E]
  ): void;
  public track(event: string, properties?: Record<string, unknown>): void;
  public track(event: string, properties: Record<string, unknown> = {}): void {
    // Always log to console in debug mode (even if telemetry is disabled)
    if (this.config.debug || !this.config.enabled) {
      console.info('[telemetry:event]', event, properties);
    }

    // Skip if disabled
    if (!this.config.enabled) {
      return;
    }

    // Sanitize data to remove any PII
    const sanitizedProps = sanitizeEventData(properties);

    // Enrich with standard properties
    const enrichedProperties: Record<string, unknown> = {
      ...sanitizedProps,
      timestamp: new Date().toISOString(),
      environment: this.config.environment,
      sessionId: this.sessionId,
      ...(this.currentUserId && { userId: this.currentUserId })
    };

    // If not initialized yet, queue the event
    if (!this.initialized) {
      this.eventQueue.push({ event, properties: enrichedProperties });
      return;
    }

    this.trackInternal(event, enrichedProperties);
  }

  /**
   * Identify the current user
   * Call this after authentication
   * 
   * @param userId - User's unique ID (UUID only, no email)
   * @param traits - Optional user traits (workspace, role, etc.)
   */
  public identify(userId: string, traits?: UserTraits): void {
    if (!userId) {
      return;
    }

    this.currentUserId = userId;

    if (!this.config.enabled || !this.posthog) {
      if (this.config.debug) {
        console.info('[telemetry:identify]', userId, traits);
      }
      return;
    }

    try {
      // Sanitize traits to ensure no PII
      const sanitizedTraits = traits ? sanitizeEventData(traits) : undefined;

      this.posthog.identify(userId, sanitizedTraits);

      if (this.config.debug) {
        console.info('[telemetry:identify]', userId, sanitizedTraits);
      }
    } catch (error) {
      console.warn('[telemetry] Identify failed:', error);
    }
  }

  /**
   * Reset the current user (call on logout)
   */
  public reset(): void {
    this.currentUserId = null;
    this.sessionId = this.generateSessionId();

    if (!this.config.enabled || !this.posthog) {
      return;
    }

    try {
      this.posthog.reset();
    } catch (error) {
      console.warn('[telemetry] Reset failed:', error);
    }
  }

  /**
   * Enable or disable telemetry at runtime
   * Useful for user privacy preferences
   */
  public setEnabled(enabled: boolean): void {
    if (!this.posthog) {
      return;
    }

    try {
      if (enabled) {
        this.posthog.opt_in_capturing();
      } else {
        this.posthog.opt_out_capturing();
      }
    } catch (error) {
      console.warn('[telemetry] setEnabled failed:', error);
    }
  }

  /**
   * Check if a feature flag is enabled (PostHog feature flags)
   */
  public isFeatureEnabled(flagKey: string): boolean {
    if (!this.config.enabled || !this.posthog) {
      return false;
    }

    try {
      return this.posthog.isFeatureEnabled(flagKey);
    } catch {
      return false;
    }
  }

  /**
   * Check if telemetry is currently enabled
   */
  public isEnabled(): boolean {
    return this.config.enabled;
  }

  /**
   * Get current environment
   */
  public getEnvironment(): string {
    return this.config.environment;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// EXPORTS
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Singleton telemetry client instance
 * Import this for direct access to tracking methods
 */
export const telemetry = TelemetryClient.getInstance();

/**
 * Export class for testing purposes
 */
export { TelemetryClient };

/**
 * Convenience method for tracking events
 */
export function track<E extends keyof TelemetryEventPayloads>(
  event: E,
  properties: TelemetryEventPayloads[E]
): void;
export function track(event: string, properties?: Record<string, unknown>): void;
export function track(event: string, properties?: Record<string, unknown>): void {
  telemetry.track(event, properties || {});
}

/**
 * Convenience method for identifying users
 */
export function identify(userId: string, traits?: UserTraits): void {
  telemetry.identify(userId, traits);
}

/**
 * Convenience method for resetting user
 */
export function reset(): void {
  telemetry.reset();
}
